// AppearAsEditor.jsx

import React, { useCallback, useMemo } from 'react';

// Utilities
import { pluralizeTitle } from 'utilities/Languages';
import {
  allocateAppearAsKey,
  applyAppearAsFilterDraft,
  COLLECTION_SURFACE_OVERVIEW,
  COLLECTION_SURFACE_RECORDS,
  createBlankAppearAsRow,
  getAppearAsFilterField,
  getAppearAsFilterFields,
  getAppearAsValue,
  getTypeAppearAsDraftRows,
  resolveAppearAsCollectionSurface,
} from 'utilities/typeAppearPolicy';

// CSS
import './AppearAsEditor.css';

// Components
import Button from 'components/button/Button';
import Checkbox from 'components/checkbox/Checkbox';
import IconPickerButton from 'components/iconpicker/IconPickerButton';
import SelectInput from 'components/selectinput/SelectInput';
import TextInput from 'components/textinput/TextInput';

/**
 * AppearAsEditor
 *
 * Edits named Appear-as rows for a type (sidebar + add-menu faces).
 *
 * @param {object[]} [rows=[]] - Current appearAs rows.
 * @param {function} onChange - Called with the next rows array.
 * @param {object[]} [schemaFields=[]] - Type schema fields for the value picker.
 * @param {boolean} [hasOverview=false] - Whether this type has an Overview surface.
 * @param {boolean} [disabled=false] - Whether the editor is read-only.
 *
 * @returns {ReactNode}
 */
const AppearAsEditor = ({
  rows = [],
  onChange,
  schemaFields = [],
  hasOverview = false,
  disabled = false,
}) => {

  // ----- MEMOS ----- //

  const draftRows = useMemo(() => getTypeAppearAsDraftRows(rows), [rows]);
  const filterFields = useMemo(() => getAppearAsFilterFields(schemaFields), [schemaFields]);
  const fieldOptions = useMemo(() => ([
    { value: '', label: 'Select a field' },
    ...filterFields.map((field) => ({
      value: field.name,
      label: field.label,
    })),
  ]), [filterFields]);

  // ----- HANDLERS ----- //

  /**
   * emitRows
   *
   * @param {object[]} nextRows
   */
  const emitRows = useCallback((nextRows) => {
    onChange?.(getTypeAppearAsDraftRows(nextRows));
  }, [onChange]);

  /**
   * handlePatchRow
   *
   * @param {number} index
   * @param {object} patch
   */
  const handlePatchRow = useCallback((index, patch) => {
    emitRows(draftRows.map((row, rowIndex) => (
      rowIndex === index ? { ...row, ...patch } : row
    )));
  }, [draftRows, emitRows]);

  /**
   * handleAddRow
   */
  const handleAddRow = useCallback(() => {
    emitRows([
      ...draftRows,
      createBlankAppearAsRow(draftRows.map((row) => row.key)),
    ]);
  }, [draftRows, emitRows]);

  /**
   * handleRemoveRow
   *
   * @param {number} index
   */
  const handleRemoveRow = useCallback((index) => {
    emitRows(draftRows.filter((_, rowIndex) => rowIndex !== index));
  }, [draftRows, emitRows]);

  /**
   * handleLabelChange
   *
   * @param {number} index
   * @param {string} nextLabel
   */
  const handleLabelChange = useCallback((index, nextLabel) => {
    const row = draftRows[index];
    if (!row) return;
    const existingKeys = draftRows
      .filter((_, rowIndex) => rowIndex !== index)
      .map((entry) => entry.key);
    const nextKey = row.label
      ? row.key
      : allocateAppearAsKey(nextLabel, existingKeys);
    const nextPlural = row.labelPlural && row.labelPlural !== pluralizeTitle(row.label)
      ? row.labelPlural
      : (nextLabel ? pluralizeTitle(nextLabel) : '');
    handlePatchRow(index, {
      key: nextKey,
      label: nextLabel,
      labelPlural: nextPlural,
    });
  }, [draftRows, handlePatchRow]);

  // ----- RENDER ----- //

  return (
    <div className="appear-as-editor-container">

      {/* HEADER */}
      <div className="appear-as-editor-header">
        <div className="appear-as-editor-title">Appear as</div>
        <Button
          iconKey="add"
          label="Add appearance"
          enabled={!disabled}
          onClick={handleAddRow}
        />
      </div>

      <div className="appear-as-editor-hint">
        Same type, different names. Add starts this field at the value you pick.
        Opening the menu pre-selects that same filter; you can change it.
      </div>

      {draftRows.length === 0 ? (
        <div className="appear-as-editor-empty">
          No appearances yet. Add Lead and Project here if they share one type.
        </div>
      ) : null}

      {/* ROWS */}
      {draftRows.map((row, index) => {
        const fieldName = getAppearAsFilterField(row);
        const selectedField = filterFields.find((field) => field.name === fieldName) || null;
        const selectedValue = getAppearAsValue(row);
        const valueOptions = selectedField?.options || [];

        return (
          <div
            className="appear-as-editor-row"
            key={row.key || `appear-${index}`}
          >

            {/* IDENTITY */}
            <div className="appear-as-editor-identity">
              <IconPickerButton
                selectedIconKey={row.iconKey || 'deployed_code'}
                disabled={disabled}
                buttonSize={36}
                iconSize={20}
                onSelect={(nextIconKey) => handlePatchRow(index, { iconKey: nextIconKey })}
              />
              <TextInput
                placeholder="Lead"
                value={row.label}
                disabled={disabled}
                onChange={(event) => handleLabelChange(index, event.target.value)}
              />
              <TextInput
                placeholder="Leads"
                value={row.labelPlural}
                disabled={disabled}
                onChange={(event) => handlePatchRow(index, { labelPlural: event.target.value })}
              />
              <Button
                iconKey="delete"
                label="Remove"
                enabled={!disabled}
                onClick={() => handleRemoveRow(index)}
              />
            </div>

            {/* VISIBILITY */}
            <div className="appear-as-editor-visibility">
              <Checkbox
                value={row.showInMenu !== false}
                size="16"
                label="Show in Menu"
                onChange={() => {
                  if (disabled) return;
                  handlePatchRow(index, { showInMenu: row.showInMenu === false });
                }}
              />
              <Checkbox
                value={row.showInAddMenu !== false}
                size="16"
                label="Show in Add menu"
                onChange={() => {
                  if (disabled) return;
                  handlePatchRow(index, { showInAddMenu: row.showInAddMenu === false });
                }}
              />
            </div>

            {hasOverview ? (
              <div className="appear-as-editor-field">
                <div className="appear-as-editor-label">Open on</div>
                <SelectInput
                  value={resolveAppearAsCollectionSurface(row, { hasOverview: true })}
                  options={[
                    { value: COLLECTION_SURFACE_OVERVIEW, label: 'Overview' },
                    {
                      value: COLLECTION_SURFACE_RECORDS,
                      label: row.labelPlural
                        ? `All ${row.labelPlural}`
                        : 'All records',
                    },
                  ]}
                  disabled={disabled}
                  onValueChange={(nextSurface) => {
                    handlePatchRow(index, { defaultSurface: nextSurface });
                  }}
                />
              </div>
            ) : null}

            {/* VALUE */}
            <div className="appear-as-editor-pair">
              <div className="appear-as-editor-field">
                <div className="appear-as-editor-label">Field</div>
                <SelectInput
                  value={fieldName}
                  options={fieldOptions}
                  disabled={disabled}
                  onValueChange={(nextField) => {
                    handlePatchRow(index, applyAppearAsFilterDraft(row, {
                      field: nextField,
                      value: '',
                    }));
                  }}
                />
              </div>
              <div className="appear-as-editor-field">
                <div className="appear-as-editor-label">
                  {row.label ? `Add ${row.label} starts as` : 'Value'}
                </div>
                <SelectInput
                  value={selectedValue}
                  options={[
                    { value: '', label: fieldName ? 'Select a value' : 'Choose a field first' },
                    ...valueOptions.map((option) => ({
                      value: option.value,
                      label: option.label || option.value,
                    })),
                  ]}
                  disabled={disabled || !fieldName}
                  onValueChange={(nextValue) => {
                    handlePatchRow(index, applyAppearAsFilterDraft(row, {
                      field: fieldName,
                      value: nextValue,
                    }));
                  }}
                />
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

// Dev Tools
AppearAsEditor.displayName = 'AppearAsEditor';

export default AppearAsEditor;
